import { isHideAdminPortalEnabled, isHostedLoginEnabled } from "@/lib/config";
import { languageStorage } from "@/lib/i18n/i18n";
import { clearSentryUser, setSentryUser } from "@/lib/sentry";
import {
  FronteggProvider,
  useAuth,
  useLoginWithRedirect,
} from "@frontegg/react";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState, type ReactNode } from "react";
import { FullPageSpinner } from "../components/ui/spinner";
import { LegalFooter } from "../components/ui/legal-footer/legal-footer";
import { DotGridBackground } from "../components/ui/dot-grid-background";
import { LogoutScreen } from "./logout-screen";

// theme for the embedded login box only — the hosted page is styled in the
// frontegg portal. the footer shows no login link; the box is the login.
const themeOptions = {
  loginBox: {
    // frontegg's page layer goes transparent so the animated background
    // rendered by the app shines through behind the card
    rootStyle: { backgroundColor: "transparent" },
    // dark card around the form, colors from the app's gray scale in main.css.
    // the card width lives in the shadow css below, frontegg's media queries
    // override any width set here.
    boxStyle: {
      backgroundColor: "var(--color-background-sidebar)",
      border: "1px solid var(--color-border)",
      borderRadius: "16px",
      padding: "48px",
      boxShadow: "0 8px 32px rgb(0 0 0 / 0.18)",
    },
    login: {
      // no self-service signup, the slot shows the invite note instead
      signupMessage: () => <InviteNote />,
    },
    pageFooter: () => <LegalFooter className="mt-6" />,
  },
};

// frontegg renders the login box inside a shadow root, so neither app
// stylesheets nor parts of the theme api reach it. this style tag goes
// straight into the shadow root: the card width (frontegg's media queries
// win over boxStyle), the text colors (the box brings its own white-on-dark
// palette) and chrome's autofill colors (need a rule next to the input) can
// only be set this way. css variables inherit into shadow trees, so the app
// tokens keep working.
//
// text colors belong here rather than in themeOptions.palette: frontegg runs
// its own color math over palette entries and throws on the oklch() our light
// tokens resolve to, which takes the whole box down. plain css it never parses,
// and var() re-resolves on its own when html.light flips.
const loginBoxShadowCss = `
  .item-3 {
    width: min(650px, calc(100vw - 2rem)) !important;
    max-width: calc(100vw - 2rem) !important;
  }
  .MuiCard-root {
    width: min(650px, calc(100vw - 2rem)) !important;
    max-width: min(650px, calc(100vw - 2rem)) !important;
    color: var(--color-foreground);
  }
  .MuiTypography-root,
  .MuiFormLabel-root,
  .MuiInputLabel-root,
  .MuiInputBase-input,
  .MuiLink-root,
  .MuiIconButton-root,
  .MuiButton-text,
  .MuiButton-outlined,
  :is(.MuiButton-text, .MuiButton-outlined) * {
    color: var(--color-foreground) !important;
  }
  /* the submit button sits on frontegg's blue fill, so its label stays light */
  button[type="submit"],
  button[type="submit"] *,
  .MuiButton-contained,
  .MuiButton-contained * {
    color: var(--color-white) !important;
  }
  /* validation messages keep a red, ours rather than frontegg's */
  .Mui-error,
  .Mui-error * {
    color: var(--color-foreground-critical) !important;
  }
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--color-background-neutral-hovered)
      inset !important;
    -webkit-text-fill-color: var(--color-foreground) !important;
    caret-color: var(--color-foreground) !important;
  }
`;

// shown under the login title, styled like the chat welcome note. inline
// styles because this renders inside frontegg's shadow root where app
// stylesheets don't apply; css variables still inherit through.
function InviteNote() {
  const { t } = useTranslation();
  return (
    <p
      style={{
        margin: "0.5rem auto 0",
        maxWidth: "34rem",
        textAlign: "center",
        color: "var(--color-foreground-pressed)",
        fontSize: "15px",
        lineHeight: 1.6,
      }}
    >
      <Trans
        t={t}
        i18nKey="login_invite_note"
        components={{
          mailLink: (
            <a
              href="mailto:support@edge-digital.com"
              style={{
                color: "inherit",
                textDecoration: "underline dotted",
                textUnderlineOffset: "4px",
              }}
            />
          ),
        }}
      />
    </p>
  );
}

// our own screen owns /account/logout, see logout-screen.tsx
const isLogoutRoute = window.location.pathname === "/account/logout";

// frontegg replaces the app with its login box on /account routes, so the
// animated backdrop mounts next to the provider, not inside it. auth pages
// are full page loads, a one-time path check is enough. the logout screen is
// ours in both login modes, so it always gets the backdrop.
const isAuthScreen =
  isLogoutRoute ||
  (!isHostedLoginEnabled && window.location.pathname.startsWith("/account"));

function injectLoginBoxShadowCss() {
  const root = document.getElementById(
    "frontegg-login-box-container-default",
  )?.shadowRoot;
  if (!root) return false;
  if (!root.getElementById("soulbyte-login-box-css")) {
    const style = document.createElement("style");
    style.id = "soulbyte-login-box-css";
    style.textContent = loginBoxShadowCss;
    root.appendChild(style);
  }
  return true;
}

// retries until frontegg has mounted the box, then injects once
function useLoginBoxShadowCss() {
  useEffect(() => {
    if (isHostedLoginEnabled || injectLoginBoxShadowCss()) return;
    const timer = setInterval(() => {
      if (injectLoginBoxShadowCss()) clearInterval(timer);
    }, 200);
    return () => clearInterval(timer);
  }, []);
}

// german texts for the embedded login box — frontegg only ships english.
// the active language follows the app's stored language, german by default.
const localizations = {
  defaultLanguage: (languageStorage.getLanguage() ?? "de") as "de" | "en",
  de: {
    loginBox: {
      login: {
        title: "Anmelden",
        signUpMessage: "Noch kein Konto?",
        signUpLink: "Registrieren",
        emailInputLabel: "E-Mail",
        emailInputPlaceholder: "name@beispiel.de",
        emailIsRequired: "E-Mail ist erforderlich",
        emailMustBeValid: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
        continue: "Fortsetzen",
        login: "Anmelden",
        signInWithPasskey: "Mit Passkey anmelden",
        signInWithSocialLogin: "oder anmelden mit",
        forgotPassword: "Passwort vergessen?",
        backToLogin: "Zurück zur Anmeldung",
        passwordInputLabel: "Passwort",
        passwordInputPlaceholder: "Passwort",
        passwordIsRequired: "Passwort ist erforderlich",
      },
      // brand names stay untranslated, frontegg's auto-translate
      // would otherwise turn Apple into "Apfel"
      socialLogins: {
        apple: "Apple",
      },
    },
  },
};

export function AuthProvider({ children }: { children: ReactNode }) {
  // frontegg drives this via customLoader so we render our own loader instead of its built-in one
  const [isFronteggLoading, setIsFronteggLoading] = useState(true);
  const [isSignedIn, setIsSignedIn] = useState(false);
  useLoginBoxShadowCss();

  // the backdrop is fixed, so it paints over the app's static content. it has to
  // go the moment a session exists, or it hides the company chooser, which still
  // renders while the path sits on /account.
  const showBackdrop = isAuthScreen && !isSignedIn;

  return (
    <>
      {showBackdrop && <DotGridBackground className="fixed inset-0" />}
      <FronteggProvider
        authOptions={{
          // Keeps the session alive by refreshing the JWT in the background
          keepSessionAlive: true,
          // google social login redirects to /oauth/account/social/success, but
          // frontegg's own router defaults to /account/social/success and matches
          // exactly, so it 404s. register the real path so the provider completes
          // the login instead of throwing "No route matches".
          routes: {
            socialLoginCallbackUrl: "/oauth/account/social/success",
            // the provider hides app content on every path in this list, so
            // its logout route moves off /account/logout to leave that page
            // to our own logout screen
            logoutUrl: "/account/frontegg-logout",
          },
        }}
        customLoader={setIsFronteggLoading}
        themeOptions={isHostedLoginEnabled ? undefined : themeOptions}
        localizations={isHostedLoginEnabled ? undefined : localizations}
        hostedLoginBox={isHostedLoginEnabled}
        contextOptions={{
          baseUrl: import.meta.env.VITE_FRONTEGG_BASE_URL,
          clientId: import.meta.env.VITE_FRONTEGG_CLIENT_ID,
          appId: import.meta.env.VITE_FRONTEGG_APP_ID,
        }}
        lazyLoadAdminPortal={!isHideAdminPortalEnabled}
        // Enable entitlements/permissions
        entitlementsOptions={{ enabled: true }}
      >
        {isLogoutRoute ? (
          <LogoutScreen />
        ) : (
          <AuthProviderContent onSignedInChange={setIsSignedIn}>
            {children}
          </AuthProviderContent>
        )}
      </FronteggProvider>

      {/* the logout screen keeps the bare backdrop while it signs the user out,
          the spinner would only cover it */}
      {isFronteggLoading && !isLogoutRoute && (
        <FullPageSpinner className="bg-background fixed inset-0 z-50" />
      )}
    </>
  );
}

function AuthProviderContent({
  children,
  onSignedInChange,
}: {
  children: ReactNode;
  onSignedInChange: (isSignedIn: boolean) => void;
}) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const loginWithRedirect = useLoginWithRedirect();
  const isSignedIn = user && !isLoading && isAuthenticated;

  // the backdrop sits outside the provider and cannot see the session itself
  useEffect(() => {
    onSignedInChange(Boolean(isSignedIn));
  }, [isSignedIn, onSignedInChange]);

  // Set Sentry user context when user changes
  useEffect(() => {
    if (isSignedIn) {
      setSentryUser(user);
    } else {
      clearSentryUser();
    }
  }, [user, isSignedIn]);

  // Send unauthenticated users to login. Hosted mode bounces to frontegg's hosted
  // page; embedded mode sends them to the in-app box — but skip /account (box
  // already showing) and /oauth (frontegg is mid code-exchange, bouncing now
  // drops the code).
  useEffect(() => {
    if (isLoading || isSignedIn) return;
    if (isHostedLoginEnabled) {
      loginWithRedirect();
      return;
    }
    const path = window.location.pathname;
    if (path.startsWith("/account") || path.startsWith("/oauth")) return;
    window.location.href = "/account/login";
  }, [isLoading, isSignedIn, loginWithRedirect]);

  // until signed in, render nothing so the login box owns the screen
  if (!isSignedIn) {
    return null;
  }

  return children;
}
